import { HistoryMessageItem, IMFile, MessageIncomingEvent, MessageOutgoingEvent, PinnedMessage } from '@wui/im'

import {
  formatVoiceDuration,
  getCachedVoiceDurationSec,
  VOICE_MESSAGE_FILE_PREFIX,
} from '@/entities/voice-message'

import { useLocalization } from '@/shared/composables/localization'
import { isAudioFile, isImageFile, isVideoFile } from '@/shared/utils/files'
import { sanitizeToText } from '@/shared/utils/sanitize'
import { getFullURL } from '@/shared/utils/url'
import { isVCardFile } from '@/shared/utils/vcard'

export type PinnedPreviewIcon = 'fileM' | 'videoM' | 'micM' | 'userRoundM'

export type PinnedPreview = {
  text: string
  icon?: PinnedPreviewIcon
  thumbnailUrl: string | undefined
}

export type GetPinnedPreviewOptions = {
  /** Длительность голоса в секундах (из декода/плеера), если нет в payload */
  voiceDurationSec?: number
}

const getFiles = (
  message?: HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>,
  pin?: PinnedMessage,
): IMFile[] => {
  const fromMessage = message?.item.files?.length
    ? message.item.files
    : message?.item.forwarding?.forwardingFiles
  const fromPin = pin?.extendedParams?.files?.length
    ? pin.extendedParams.files
    : pin?.extendedParams?.forwardingFiles
  const files = fromMessage ?? fromPin ?? []
  return files.filter((file): file is IMFile => Boolean(file?.filename))
}

export const isPinnedVoiceFile = (file: IMFile) => {
  return file.filemime?.includes('audio/')
    || file.filename.toLowerCase().startsWith(VOICE_MESSAGE_FILE_PREFIX)
    || isAudioFile(file.filename)
}

const getMessageText = (
  message?: HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>,
  pin?: PinnedMessage,
) => {
  // У пересланных приоритет у forwardingText (тело), а не messageText (приписка)
  const fromMessage = message?.item.forwarding?.forwardingText
    || message?.item.messageText
    || ''
  const fromPin = pin?.extendedParams?.forwardingText || pin?.message || ''
  return sanitizeToText((fromMessage || fromPin).trim())
}

export const getPinnedVoiceFileCookie = (
  pin: PinnedMessage,
  message?: HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>,
): string | undefined => {
  const voiceFile = getFiles(message, pin).find((file) => isPinnedVoiceFile(file))
  return voiceFile?.filecookie
}

const resolveVoiceDurationSec = (
  file: IMFile,
  voiceDurationSec?: number,
) => {
  if (voiceDurationSec && voiceDurationSec > 0) {
    return voiceDurationSec
  }

  const fromFile = Number((file as IMFile & { duration?: number }).duration ?? 0)
  if (fromFile > 0) {
    return fromFile
  }

  if (!file.filecookie) {
    return 0
  }

  return getCachedVoiceDurationSec(file.filecookie)
}

const getContactDisplayName = (file: IMFile) => {
  const name = file.filename.replace(/\.vcf$/i, '').trim()
  return name || file.filename
}

export const getPinnedPreview = (
  pin: PinnedMessage,
  message?: HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>,
  options?: GetPinnedPreviewOptions,
): PinnedPreview => {
  const { t } = useLocalization()
  const text = getMessageText(message, pin)
  const files = getFiles(message, pin)
  const images = files.filter((file) => isImageFile(file.filename))
  const audios = files.filter((file) => isPinnedVoiceFile(file))
  const videos = files.filter((file) => isVideoFile(file.filename) && !isPinnedVoiceFile(file))
  const contacts = files.filter((file) => isVCardFile(file))
  const otherFiles = files.filter((file) => (
    !isImageFile(file.filename)
    && !isVideoFile(file.filename)
    && !isPinnedVoiceFile(file)
    && !isVCardFile(file)
  ))

  const thumbnailUrl = images[0]?.filecookie
    ? getFullURL(`/files/${images[0].filecookie}`)
    : undefined

  if (text && otherFiles.length) {
    return { text, icon: 'fileM', thumbnailUrl }
  }

  if (text && videos.length) {
    return { text, icon: 'videoM', thumbnailUrl }
  }

  if (text) {
    return { text, thumbnailUrl }
  }

  if (contacts.length === 1 && files.length === 1) {
    return {
      text: getContactDisplayName(contacts[0]!),
      icon: 'userRoundM',
      thumbnailUrl: undefined,
    }
  }

  if (images.length && !videos.length && !audios.length && !otherFiles.length && !contacts.length) {
    if (images.length === 1) {
      return { text: t('chat.pinnedImage'), thumbnailUrl }
    }

    return {
      text: t('chat.pinnedImagesCount', { count: images.length }),
      thumbnailUrl,
    }
  }

  if (videos.length && !images.length && !audios.length && !otherFiles.length && !contacts.length) {
    if (videos.length === 1) {
      return {
        text: videos[0]?.filename ?? t('common.videos').toLocaleLowerCase(),
        icon: 'videoM',
        thumbnailUrl: undefined,
      }
    }

    return {
      text: t('chat.pinnedVideosCount', { count: videos.length }),
      icon: 'videoM',
      thumbnailUrl: undefined,
    }
  }

  if (audios.length && !images.length && !videos.length && !otherFiles.length && !contacts.length) {
    const duration = resolveVoiceDurationSec(audios[0]!, options?.voiceDurationSec)
    return {
      text: duration > 0
        ? t('chat.pinnedVoiceMessage', { duration: formatVoiceDuration(duration * 1000) })
        : t('chat.pinnedVoiceMessageEmpty'),
      icon: 'micM',
      thumbnailUrl: undefined,
    }
  }

  if (otherFiles.length === 1 && files.length === 1) {
    return { text: otherFiles[0]?.filename ?? '', icon: 'fileM', thumbnailUrl: undefined }
  }

  if (otherFiles.length > 1 && !images.length && !videos.length && !audios.length && !contacts.length) {
    return {
      text: t('chat.pinnedFilesCount', { count: otherFiles.length }),
      icon: 'fileM',
      thumbnailUrl: undefined,
    }
  }

  if (files.length > 1) {
    return {
      text: t('chat.pinnedFilesCount', { count: files.length }),
      icon: 'fileM',
      thumbnailUrl,
    }
  }

  if (files.length === 1) {
    return {
      text: files[0]?.filename ?? '',
      thumbnailUrl,
    }
  }

  return { text: '', thumbnailUrl }
}

export type PinnedSystemQuote = {
  text: string
  /** true — реальный текст/приписка сообщения; в системке оборачиваем в «» */
  isMessageText: boolean
}

/** Текст превью закрепа для системных нотификаций (без иконок) и chat-list */
export const getPinnedSystemQuote = (
  pin: PinnedMessage | undefined,
  message?: HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>,
  options?: GetPinnedPreviewOptions,
): PinnedSystemQuote => {
  if (!pin && !message) {
    return { text: '', isMessageText: false }
  }

  const fallbackFiles = message?.item.files ?? message?.item.forwarding?.forwardingFiles
  const fallbackPin: PinnedMessage = pin ?? {
    eventId: message?.eventId ?? 0,
    message: '',
    pServedUserExecuter: message?.item.executor ?? '',
    rank: message?.rank ?? 0,
    timestamp: message?.timestamp ?? 0,
    extendedParams: {
      sender: message?.item.executor ?? '',
      ...(fallbackFiles?.length ? { files: fallbackFiles } : {}),
    },
  }

  const messageText = getMessageText(message, fallbackPin)
  return {
    text: getPinnedPreview(fallbackPin, message, options).text,
    isMessageText: Boolean(messageText),
  }
}
